import { createApi } from "@reduxjs/toolkit/query/react";
import { IPaginationPayload } from "../../interfaces/shared/IPaginationPayload";
import reservationAxiosQuery from "../../utils/axios/reservation.axios";
import { PaginatedReservations } from "../../interfaces/reservations/paginatedUser.type";
import { PaginatedRoomRequests } from "../../interfaces/room-requests/paginatedRoomRequests.type";
import { IOutBoundPayload } from "../../interfaces/reservations/IOutboundPayload";
import { IEmailReservations } from "../../interfaces/reservations/IEmailReservations";
import { GOOGLE_REFRESH_TOKEN } from "../../config";
import { PaginatedCalls } from "../../interfaces/reservations/paginatedCalls.type";
import { IAgentCallResponse } from "../../interfaces/reservations/ICalls";
import { AxiosResponse } from "axios";
import { PaginatedDrafts } from "../../interfaces/reservations/paginationDraft.type";
import { IDraftProps } from "../../interfaces/reservations/IDraftProps";

export const reservationsAPI = createApi({
  reducerPath: "reservations",
  baseQuery: reservationAxiosQuery(),
  tagTypes: ["reservations"],
  endpoints: (builder) => ({
    getAllReservations: builder.query<
      PaginatedReservations,
      IPaginationPayload
    >({
      query: ({ filters, pageNumber, pageSize, data }: IPaginationPayload) => ({
        url: filters
          ? `/reservations/all?page=${pageNumber}&size=${pageSize}&filters=${filters}`
          : `/reservations/all?page=${pageNumber}&size=${pageSize}`,
        method: "PUT",
        data: data
      }),
    }),
    getAllRoomsRequests: builder.query<
      PaginatedRoomRequests,
      IPaginationPayload
    >({
      query: ({ filters, pageNumber, pageSize }: IPaginationPayload) => ({
        url: filters
          ? `/rooms/requests?page=${pageNumber}&size=${pageSize}&filters=${filters}`
          : `/rooms/requests?page=${pageNumber}&size=${pageSize}`,
        method: "GET",
      }),
    }),
    getAllDrafts: builder.query<PaginatedDrafts, IPaginationPayload>({
      query: ({ filters, pageNumber, pageSize }: IPaginationPayload) => ({
        url:
          filters && filters !== "all"
            ? `/agent-reservations/draft?page=${pageNumber}&limit=${pageSize}&status=${filters}`
            : `/agent-reservations/draft?page=${pageNumber}&limit=${pageSize}`,
        method: "GET",
      }),
    }),
    acceptOrRejectDraft: builder.mutation<void, IDraftProps>({
      query: ({ id, emailId, response, rejectReason }) => ({
        url: `/agent-reservations/respond`,
        method: "POST",
        data: rejectReason
          ? { id, emailId, response, accessToken: window.localStorage.getItem('google_access_token'), rejectReason }
          : { id, emailId, response, accessToken: window.localStorage.getItem('google_access_token') },
      }),
    }),
    postOutboundCall: builder.mutation<PaginatedRoomRequests, IOutBoundPayload>(
      {
        query: (data: IOutBoundPayload) => ({
          url: `/ai-voice-calling/call/outbound`,
          method: "POST",
          data,
        }),
      }
    ),
    getGuestDetailsInfo: builder.mutation<IEmailReservations, string>({
      query: (name: string) => ({
        url: `/ai-voice-calling/user/search`,
        method: "PUT",
        data: {
          name: name
        }
      }),
    }),
    confirmGuestInfo: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `/reservations/assign/guest-profile`,
        method: "PUT",
        data: data
      }),
    }),
    getReservationPaymentsInfo: builder.query<any, string>({
      query: (reservationId: string) => ({
        url: `/reservation-payments/${reservationId}/room-services`,
        method: "PUT",
      }),
    }),
    getAllReservationsEmail: builder.mutation<IEmailReservations, string>({
      query: (accessToken: string) => ({
        url: `/agent-reservations/reservation-email?accessToken=${accessToken}`,
        method: "GET",
      }),
    }),
    getAllDraftsEmail: builder.mutation<IEmailReservations, string>({
      query: (accessToken: string) => ({
        url: `/agent-reservations/get-summay-emails?accessToken=${accessToken}`,
        method: "POST",
      }),
    }),
    getAllInvoicesEmail: builder.mutation<IEmailReservations, string>({
      query: (accessToken: string) => ({
        url: `/agent-reservations/emails?accessToken=${accessToken}`,
        method: "GET",
      }),
    }),
    getAccessToken: builder.query<{ tokens: { access_token: string } }, void>({
      query: () => ({
        url: "/gmail/refresh-token",
        method: "POST",
        data: {
          refreshToken: GOOGLE_REFRESH_TOKEN,
        },
      }),
    }),
    getAllCalls: builder.query<PaginatedCalls, IPaginationPayload>({
      query: ({ filters, pageNumber, pageSize }: IPaginationPayload) => ({
        url: filters
          ? `/calls?page=${pageNumber}&size=${pageSize}&filters=${filters}`
          : `/calls?page=${pageNumber}&size=${pageSize}`,
        method: "GET",
      }),
    }),
    getCallDetails: builder.query<
      AxiosResponse<IAgentCallResponse | null>,
      string
    >({
      query: (id) => ({
        url: `/ai-voice-calling/call/${id}`,
        method: "GET",
      }),
    }),
    getAuthUrl: builder.query<{authUrl2: string}, void>({
      query: () => ({
        url: `/gmail/auth-url`,
        method: "GET",
      }),
    }),
	getCurrentAuthUrl: builder.query({
		query: () => ({
		  url: `/gmail/auth-url`,
		  method: "GET",
		}),
	  }),
	  getOutlookAuthUrl: builder.query({
		query: () => ({
		  url: `/outlook/auth-url`,
		  method: "GET",
		}),
	  }),
    getEmails: builder.mutation({
      query: (accessToken: any) => ({
        url: `/agent-reservations/reservation-email?accessToken=${accessToken}`,
        method: "GET",
      }),
    }),
    //
  }),
});

export const {
  useGetAllReservationsQuery,
  useGetAllRoomsRequestsQuery,
  usePostOutboundCallMutation,
  useGetAllInvoicesEmailMutation,
  useGetAccessTokenQuery,
  useGetAllCallsQuery,
  useGetCallDetailsQuery,
  useGetAllDraftsQuery,
  useGetAuthUrlQuery,
  useAcceptOrRejectDraftMutation,
  useGetEmailsMutation,
  useGetCurrentAuthUrlQuery,
  useGetOutlookAuthUrlQuery,
  useGetAllDraftsEmailMutation,
  useGetGuestDetailsInfoMutation,
  useGetReservationPaymentsInfoQuery,
  useConfirmGuestInfoMutation
} = reservationsAPI;
